import React, { useEffect, useState } from 'react';
import { motion, Variants } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';

export function Intro() {
  const [content, setContent] = useState({
    intro_eyebrow: 'Welcome to Walnut Farm Retreat',
    intro_left_headline: 'Your Perfect Getaway',
    intro_left_description: 'At Walnut Farm, we offer a serene farmhouse experience with three cozy rooms, a refreshing pool, and an open kitchen, perfect for your next getaway or family gathering.'
  });

  useEffect(() => {
    const fetchContent = async () => {
      const { data } = await supabase
        .from('page_content')
        .select('key, content')
        .in('key', ['intro_eyebrow', 'intro_left_headline', 'intro_left_description']);
      
      if (data) {
        const newContent = { ...content };
        data.forEach(item => {
          if (item.key in newContent) {
            newContent[item.key as keyof typeof content] = item.content;
          }
        });
        setContent(newContent);
      }
    };
    fetchContent();
  }, []);

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1.2,
        ease: "easeOut",
      },
    },
  };

  return (
    <section id="farm" className="pt-24 lg:pt-40 pb-12 lg:pb-20 bg-secondary px-6 overflow-hidden">
      <div className="max-w-screen-xl mx-auto">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="flex flex-col items-center text-center"
        >
          <motion.div variants={itemVariants} className="mb-20 lg:mb-32">
            <h2 className="heading-luxury">
              {content.intro_eyebrow}
            </h2>
            <div className="mt-8 flex justify-center items-center gap-4">
              <div className="h-[1px] w-12 bg-accent/30" />
              <div className="w-1.5 h-1.5 rounded-full bg-accent" />
              <div className="h-[1px] w-12 bg-accent/30" />
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-24 text-left relative">
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-[1px] bg-gradient-to-b from-transparent via-accent/10 to-transparent -translate-x-1/2" />

            <motion.div variants={itemVariants} className="space-y-6 md:pr-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-[1px] bg-primary/40" />
                <h3 className="text-xl md:text-3xl font-serif italic !text-[#176B5B]">
                  {content.intro_left_headline}
                </h3>
              </div>
              <p className="font-clean text-base md:text-lg leading-relaxed !text-[#59635C]">
                {content.intro_left_description}
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-6 md:pl-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-[1px] bg-primary/40" />
                <h3 className="text-xl md:text-3xl font-serif italic !text-[#176B5B]">
                  Relax and Unwind
                </h3>
              </div>
              <p className="font-clean text-base md:text-lg leading-relaxed !text-[#59635C]">
                Our farmhouse is designed for comfort and relaxation, providing a tranquil 
                escape from the hustle and bustle of everyday life. Enjoy nature and create 
                lasting memories with loved ones.
              </p>
            </motion.div>
          </div>

          <motion.div variants={itemVariants} className="mt-24 opacity-30">
             <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-primary">
                <path d="M20 5C20 5 20 15 10 15C10 15 20 15 20 25C20 25 20 15 30 15C30 15 20 15 20 5Z" stroke="currentColor" strokeWidth="1"/>
                <path d="M20 35V25" stroke="currentColor" strokeWidth="1"/>
             </svg>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
